import * as _ from 'lodash';
import { useState } from 'react';
import { FormGroup } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';
import type { SortingRule } from 'react-table';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import ButtonGroup from '../../../generic/Button/ButtonGroup';
import IconButton from '../../../generic/Button/IconButton';
import Flex from '../../../generic/Flex';
import SearchInput from '../../../generic/Forms/SearchInput';
import { icons } from '../../../generic/Icon/icons';
import Loader from '../../../generic/Loader';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Text from '../../../generic/Text';
import {
  CreatedCell,
  ExpirationCell,
  LastUsedCell,
  TokenCell,
  TokenNameCell,
} from '../../../shared/portalTokenTable/cells';
import Header from '../../../shared/settings/SettingsPageHeader';
import type { PortalToken } from '../../../../../shared/publicPortal/types';

import usePortalTokenSettings from './hooks/usePortalTokenSettings';
import ModalCreatePortalToken from './ModalCreatePortalToken';
import ModalEditPortalToken from './ModalEditPortalToken';
import type { ModalMode } from './ModalCreatePortalToken';

import styles from './SettingsOrganizationPortal.module.less';

const ReleaseGroupsCell: VoidFunctionComponent<{ original: PortalToken }> = ({ original }) => {
  const { releaseGroups } = original;
  if (releaseGroups.length === 0) {
    return null;
  }
  // It is important to dedupe the release group titles
  // because the same release group might appear multiple times for different purposes (full access vs. partial access).
  const releaseGroupTitles = _.uniq(releaseGroups.map((rg) => rg.title));
  const firstReleaseGroupName = releaseGroupTitles[0];
  if (releaseGroupTitles.length === 1) {
    return <span>{firstReleaseGroupName}</span>;
  }

  return (
    <span>
      {firstReleaseGroupName}, +{releaseGroups.length - 1} more
    </span>
  );
};

const ActionsCell: VoidFunctionComponent<{
  original: PortalToken;
  setEditingToken: (portalToken: PortalToken) => void;
  onDeleteToken: (portalToken: PortalToken) => void;
}> = ({ original, setEditingToken, onDeleteToken }) => (
  <ButtonGroup spacing="xs">
    <IconButton icon={icons.edit} id="edit" onClick={() => setEditingToken(original)} />
    <IconButton icon={icons.trashRegular} id="delete" onClick={() => onDeleteToken(original)} />
  </ButtonGroup>
);

const defaultSortMode: SortingRule = { id: 'createdAt', desc: true };

const AccessTokens: VoidFunctionComponent = () => {
  const organizationId = window.user?.organizationId as number;
  const {
    isLoading,
    settings: tokens,
    setSearch,
    search,
    errors,
    onCreateToken,
    createErrors,
    onResetCreateErrors,
    isCreating,
    isEditing,
    onEditToken,
    editErrors,
    onResetEditErrors,
    onDeleteToken,
  } = usePortalTokenSettings(organizationId);
  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode);
  const [modalMode, setModalMode] = useState<ModalMode>('Hide');
  const [editingToken, setEditingToken] = useState<PortalToken | null>(null);
  const { onConfirm } = useDialogContext();

  const onCreateNewTokenClick = () => {
    setModalMode('Create');
  };

  const onHideModal = () => {
    setModalMode('Hide');
    onResetCreateErrors();
  };

  let combinedErrors: string[] | null = [
    ...(createErrors || []),
    ...(editErrors || []),
  ];
  if (combinedErrors.length === 0) {
    combinedErrors = null;
  }

  const onDeleteTokenWithConfirmation = (token: PortalToken) => {
    return onConfirm({
      title: 'Delete Token',
      body: `Are you sure you want to delete ${token.name}?`,
      onConfirm: () => {
        return onDeleteToken(token.id);
      },
    });
  };

  return (
    <>
      <div>
        <Text.H3>Access Tokens</Text.H3>
        <Flex justify="space-between" align="flex-start">
          <Text.Paragraph>Manage access to private release groups.</Text.Paragraph>
          <Button onClick={onCreateNewTokenClick} size={BUTTON_SIZE.LARGE} type={BUTTON_TYPE.SECONDARY}>
            Create New Token
          </Button>
        </Flex>
        <FormGroup className={styles.textInputFormGroup}>
          <SearchInput label="search" value={search || ''} placeholder="Search" onChange={setSearch} />
        </FormGroup>
        <Header.Errors errors={errors} />
        {isLoading.any && !tokens ? (
          <Loader />
        ) : (
          <SimpleReactTable
            showPagination
            onSortedChanged={setSortMode}
            defaultSortMode={[defaultSortMode]}
            data={tokens || []}
            columns={[
              {
                id: 'name',
                sortable: true,
                Header: <TableHeader title="Name" sort={{ id: 'name', currentMode: sortMode }} />,
                accessor: 'name',
                Cell: TokenNameCell,
                resizable: true,
                width: 240,
              },
              {
                id: 'token',
                sortable: false,
                Header: <TableHeader title="Token" />,
                accessor: 'token',
                Cell: TokenCell,
                resizable: false,
                width: 120,
              },
              {
                id: 'expires',
                sortable: true,
                Header: <TableHeader title="Expires" sort={{ id: 'expires', currentMode: sortMode }} />,
                accessor: 'expiration',
                Cell: ExpirationCell,
                resizable: false,
                width: 96,
              },
              {
                id: 'lastUsed',
                sortable: true,
                Header: <TableHeader title="Last Used" sort={{ id: 'lastUsed', currentMode: sortMode }} />,
                accessor: 'lastUsed',
                Cell: LastUsedCell,
                resizable: false,
                width: 160,
              },
              {
                id: 'releaseGroups',
                sortable: true,
                Header: <TableHeader title="Release Groups" sort={{ id: 'releaseGroups', currentMode: sortMode }} />,
                accessor: 'releaseGroups',
                Cell: ReleaseGroupsCell,
                resizable: true,
              },
              {
                id: 'createdAt',
                sortable: true,
                Header: <TableHeader title="Created" sort={{ id: 'createdAt', currentMode: sortMode }} />,
                accessor: 'createdAt',
                Cell: CreatedCell,
                resizable: false,
                width: 96,
              },
              {
                style: { justifyContent: 'center' },
                id: 'actions',
                sortable: false,
                Header: <TableHeader title="Actions" center />,
                accessor: 'id',
                // eslint-disable-next-line react/no-unstable-nested-components
                Cell: ({ original }: { original: PortalToken }) => (
                  <ActionsCell
                    original={original}
                    setEditingToken={setEditingToken}
                    onDeleteToken={onDeleteTokenWithConfirmation}
                  />
                ),
                resizable: false,
                width: 120,
              },
            ]}
          />
        )}
      </div>
      <ModalCreatePortalToken
        mode={modalMode}
        isCreating={isCreating}
        errors={combinedErrors}
        onHide={onHideModal}
        onCreateToken={onCreateToken}
      />
      {editingToken && (
        <ModalEditPortalToken
          isEditing={isEditing}
          errors={combinedErrors}
          onHide={() => {
            setEditingToken(null);
            onResetEditErrors();
          }}
          onEditToken={onEditToken}
          editingToken={editingToken}
        />
      )}
    </>
  );
};

export default AccessTokens;
